// src/services/api.js
// Lightweight fetch wrapper for CRA (no axios). Includes:
// - Base URL from REACT_APP_API_URL
// - JSON handling
// - Timeout + AbortController
// - Auth token helpers (localStorage)
// - Query params & file uploads
// - Friendly ApiError

class ApiError extends Error {
  constructor(status, message, details) {
    super(message || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

const DEFAULT_TIMEOUT = 15000;

// Resolve base URL (normalize trailing slash)
const RAW_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080/api/";
const BASE = RAW_BASE.replace(/\/+$/, "") + "/";

// --- Auth token helpers (customize the storage key if you want) ---
export const getAuthToken = () => localStorage.getItem("auth_token");
export const setAuthToken = (t) => localStorage.setItem("auth_token", t);
export const clearAuthToken = () => localStorage.removeItem("auth_token");

// --- Build URL with optional query params ---
function makeURL(path = "", params) {
  const cleanPath = String(path).replace(/^\/+/, "");
  const url = new URL(cleanPath, BASE);
  if (params && typeof params === "object") {
    for (const [k, v] of Object.entries(params)) {
      if (v == null) continue;
      if (Array.isArray(v)) v.forEach((vv) => url.searchParams.append(k, vv));
      else url.searchParams.append(k, String(v));
    }
  }
  return url.toString();
}

// --- Timeout wrapper ---
function withTimeout(signal, ms = DEFAULT_TIMEOUT) {
  if (!ms || ms <= 0) return { signal, cancel: () => {} };
  const controller = new AbortController();
  const id = setTimeout(
    () => controller.abort(new DOMException("Timeout", "TimeoutError")),
    ms
  );
  const cancel = () => clearTimeout(id);

  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }

  return { signal: controller.signal, cancel };
}

// --- Core request ---
async function request(
  path,
  {
    method = "GET",
    params,
    body,
    headers = {},
    auth = true,
    timeout = DEFAULT_TIMEOUT,
    signal,
    credentials = "omit", // set to 'include' if you use cookie auth
    ...rest
  } = {}
) {
  const url = makeURL(path, params);

  const finalHeaders = new Headers({ Accept: "application/json", ...headers });

  const isForm = body instanceof FormData;
  const isBinary = body instanceof Blob || body instanceof ArrayBuffer;

  if (body != null && !isForm && !isBinary && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (auth) {
    const token = getAuthToken();
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  const payload =
    body == null ? undefined : isForm || isBinary ? body : JSON.stringify(body);

  const { signal: timeoutSignal, cancel } = withTimeout(signal, timeout);

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: payload,
      signal: timeoutSignal,
      credentials, // 'omit' | 'same-origin' | 'include'
      ...rest,
    });
  } catch (err) {
    cancel();
    if (err?.name === "AbortError" || err?.name === "TimeoutError") {
      throw new ApiError(0, "Request timed out");
    }
    throw new ApiError(0, err?.message || "Network error");
  } finally {
    cancel();
  }

  const contentType = res.headers.get("content-type") || "";
  let data = null;
  try {
    if (contentType.includes("application/json")) data = await res.json();
    else if (contentType.startsWith("text/")) data = await res.text();
    else if (res.status !== 204) data = await res.blob();
  } catch {
    // ignore parse errors, leave data as null
  }

  if (!res.ok) {
    const message = (data && (data.message || data.error)) || res.statusText;
    throw new ApiError(res.status, message, data);
  }

  return data;
}

// --- Convenience methods ---
const api = {
  request,
  get: (path, params, options) => request(path, { method: "GET", params, ...options }),
  post: (path, body, options) => request(path, { method: "POST", body, ...options }),
  put: (path, body, options) => request(path, { method: "PUT", body, ...options }),
  patch: (path, body, options) => request(path, { method: "PATCH", body, ...options }),
  delete: (path, options) => request(path, { method: "DELETE", ...options }),
  upload: (path, fileOrFormData, fields = {}, options) => {
    const form = fileOrFormData instanceof FormData ? fileOrFormData : new FormData();
    if (!(fileOrFormData instanceof FormData) && fileOrFormData) {
      form.append("file", fileOrFormData);
    }
    Object.entries(fields || {}).forEach(([k, v]) => {
      if (v != null) form.append(k, v);
    });
    return request(path, { method: "POST", body: form, ...options });
  },
};

export default api;
export { ApiError };
